import React, { useState, useEffect } from "react";
import { useAuth, getAuthToken } from "../context/AuthContext";
import {
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Clock,
  UserCheck,
  TrendingUp,
  Award,
  Layers,
  Search,
  Filter,
  Plus,
  ArrowRight,
  RotateCcw,
  Download,
  Printer,
  ChevronRight,
  Shield,
  FileText,
  Calendar,
  Users,
  Building,
  Target,
  Send,
  AlertCircle,
  HelpCircle,
  FileSpreadsheet,
  RefreshCw,
  PhoneCall,
  Flame,
  Check
} from "lucide-react";

interface InterventionKPIs {
  total_requiring_intervention: number;
  high_priority: number;
  medium_priority: number;
  low_priority: number;
  active_plans: number;
  completed_plans: number;
  improved_students: number;
  still_requiring_support: number;
  escalated_students: number;
}

interface WeakStudent {
  id: number;
  srn: string;
  student_name: string;
  father_name: string;
  board_id: number;
  class_id: number;
  board_name: string;
  class_name: string;
  section: string;
  overall_percentage: number;
  weak_subjects_count: number;
  weak_subjects: { id: number; name: string; percentage: number }[];
  attendance_percentage: number;
  priority: "HIGH" | "MEDIUM" | "LOW";
  reasons: string;
  has_active_plan: boolean;
}

interface InterventionPlan {
  id: number;
  student_id: number;
  srn: string;
  student_name: string;
  father_name: string;
  board_name: string;
  class_name: string;
  section: string;
  subject_id: number;
  subject_name: string;
  teacher_name?: string;
  assigned_teacher_id?: number;
  baseline_percentage: number;
  current_percentage: number;
  target_percentage: number;
  target_date: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  intervention_type: string;
  frequency: string;
  remarks: string;
  status: "Draft" | "Active" | "In Progress" | "Monitoring" | "Completed" | "Escalated" | "Closed";
  required_improvement: number;
  current_gain: number;
  progress_percentage: number;
  improvement_category: string;
  created_at: string;
}

export const InterventionManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("dashboard");
  const [loading, setLoading] = useState<boolean>(true);

  // Data states
  const [kpis, setKpis] = useState<InterventionKPIs | null>(null);
  const [weakStudents, setWeakStudents] = useState<WeakStudent[]>([]);
  const [plans, setPlans] = useState<InterventionPlan[]>([]);
  const [effectiveness, setEffectiveness] = useState<any>(null);
  const [remedialGroups, setRemedialGroups] = useState<any[]>([]);

  // Filter states
  const [boardFilter, setBoardFilter] = useState<string>("");
  const [classFilter, setClassFilter] = useState<string>("");
  const [classList, setClassList] = useState<Array<{ id: number; name: string }>>([]);
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const defaultClasses = [
    { id: 3, name: "Class 6" },
    { id: 4, name: "Class 7" },
    { id: 5, name: "Class 8" },
    { id: 6, name: "Class 9" },
    { id: 1, name: "Class 10" },
    { id: 7, name: "Class 11" },
    { id: 2, name: "Class 12" },
  ];

  useEffect(() => {
    fetch("/api/classes")
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.classes) && d.classes.length > 0) {
          setClassList(d.classes);
        }
      })
      .catch(() => {});
  }, []);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showFollowupModal, setShowFollowupModal] = useState<boolean>(false);
  const [showEscalateModal, setShowEscalateModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showParentModal, setShowParentModal] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<InterventionPlan | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [studentProfile, setStudentProfile] = useState<any>(null);

  // Form states
  const [formData, setFormData] = useState<any>({
    student_id: "",
    subject_id: "1",
    baseline_percentage: "38",
    target_percentage: "60",
    target_date: "2026-09-30",
    priority: "HIGH",
    intervention_type: "Remedial Teaching",
    frequency: "Daily",
    remarks: ""
  });

  const [followupForm, setFollowupForm] = useState<any>({
    followup_date: new Date().toISOString().split("T")[0],
    activity: "Remedial Class Test & Practice",
    current_percentage: "52",
    student_response: "Good understanding of core concepts",
    attendance_percentage: "88",
    teacher_remarks: "Marked progress in algebra formulas",
    next_action: "Provide weekly revision worksheets"
  });

  const [escalateForm, setEscalateForm] = useState<any>({
    escalation_date: new Date().toISOString().split("T")[0],
    reason: "Student struggling with foundational formulas after 3 remedial sessions",
    recommended_action: "Schedule 1-on-1 parent counseling and peer tutoring",
    assigned_authority: "Principal"
  });

  const [parentForm, setParentForm] = useState<any>({
    student_id: "",
    communication_date: new Date().toISOString().split("T")[0],
    mode: "Phone",
    parent_name: "",
    reason: "Academic Progress & Remedial Attendance Update",
    summary: "Informed parent about extra class schedule and math target",
    action_agreed: "Parent will monitor 1-hour home study daily"
  });

  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [kpiRes, weakRes, plansRes, effRes, groupsRes] = await Promise.all([
        fetch("/api/interventions/dashboard", { headers }).then(r => r.json()),
        fetch(`/api/interventions/students-needing-support?board_id=${boardFilter}&class_id=${classFilter}`, { headers }).then(r => r.json()),
        fetch(`/api/interventions/plans?board_id=${boardFilter}&class_id=${classFilter}&priority=${priorityFilter}&status=${statusFilter}`, { headers }).then(r => r.json()),
        fetch("/api/interventions/effectiveness", { headers }).then(r => r.json()),
        fetch("/api/interventions/remedial-groups", { headers }).then(r => r.json())
      ]);

      if (kpiRes.success) setKpis(kpiRes.kpis);
      if (weakRes.success) setWeakStudents(weakRes.students);
      if (plansRes.success) setPlans(plansRes.plans);
      if (effRes.success) setEffectiveness(effRes);
      if (groupsRes.success) setRemedialGroups(groupsRes.groups);
    } catch (err) {
      console.error("Error loading intervention data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [boardFilter, classFilter, priorityFilter, statusFilter]);

  const handleOpenProfile = async (studentId: number) => {
    setSelectedStudentId(studentId);
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/interventions/student-profile/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json());
      if (res.success) {
        setStudentProfile(res);
        setShowProfileModal(true);
      }
    } catch (err) {
      console.error("Error fetching student profile:", err);
    }
  };

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const res = await fetch("/api/interventions/plans", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      }).then(r => r.json());

      if (res.success) {
        setStatusMsg({ type: "success", text: res.message });
        setShowCreateModal(false);
        fetchAllData();
      } else {
        setStatusMsg({ type: "error", text: res.message });
      }
    } catch (err) {
      setStatusMsg({ type: "error", text: "Failed to create intervention plan." });
    }
  };

  const handleAddFollowup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/interventions/plans/${selectedPlan.id}/followups`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(followupForm)
      }).then(r => r.json());

      if (res.success) {
        setStatusMsg({ type: "success", text: `Follow-up saved! New score: ${res.current_percentage}% (${res.delta >= 0 ? "+" : ""}${res.delta}%)` });
        setShowFollowupModal(false);
        fetchAllData();
      } else {
        setStatusMsg({ type: "error", text: res.message });
      }
    } catch (err) {
      setStatusMsg({ type: "error", text: "Failed to save follow-up." });
    }
  };

  const handleEscalatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`/api/interventions/plans/${selectedPlan.id}/escalate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(escalateForm)
      }).then(r => r.json());

      if (res.success) {
        setStatusMsg({ type: "success", text: "Intervention plan escalated to Principal/Head." });
        setShowEscalateModal(false);
        fetchAllData();
      } else {
        setStatusMsg({ type: "error", text: res.message });
      }
    } catch (err) {
      setStatusMsg({ type: "error", text: "Failed to escalate plan." });
    }
  };

  const handleParentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      const res = await fetch("/api/interventions/parent-communication", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(parentForm)
      }).then(r => r.json());

      if (res.success) {
        setStatusMsg({ type: "success", text: "Parent communication record logged." });
        setShowParentModal(false);
      }
    } catch (err) {
      setStatusMsg({ type: "error", text: "Failed to log communication." });
    }
  };

  const exportData = (format: "csv" | "xlsx") => {
    window.open(`/api/interventions/export?format=${format}`, "_blank");
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Official Government Header */}
      <div className="bg-gradient-to-r from-[#0F2C59] via-[#1E3A8A] to-[#0F2C59] text-white p-6 rounded-2xl shadow-lg border-b-4 border-amber-500">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-wider">
              <Shield className="w-4 h-4" /> District School Education Department, Nuh, Haryana
            </div>
            <h1 className="text-2xl font-black mt-1 tracking-tight flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-amber-400" />
              Student Intervention & Improvement Planning
            </h1>
            <p className="text-xs text-blue-100 mt-1 max-w-2xl leading-relaxed">
              Targeted academic remediation engine for Board Exam candidates (Class 10 & 12 • HBSE & CBSE).
              Tracking baseline, diagnostic weakness, remedial targets, follow-ups, and progress delta.
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => exportData("xlsx")}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <FileSpreadsheet className="w-4 h-4" /> Export XLSX
            </button>
            <button
              onClick={() => exportData("csv")}
              className="px-3.5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={() => window.print()}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-amber-300 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-slate-700 shadow-sm transition"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 mt-6 overflow-x-auto pt-2 border-t border-blue-800/60">
          {[
            { id: "dashboard", label: "Executive Dashboard", icon: Target },
            { id: "weak-students", label: "Students Needing Support", icon: AlertTriangle },
            { id: "plans", label: "Intervention Plans", icon: Clock },
            { id: "remedial-groups", label: "Remedial Groups", icon: Users },
            { id: "calendar", label: "Schedule & Calendar", icon: Calendar },
            { id: "effectiveness", label: "Intervention Effectiveness", icon: TrendingUp },
            { id: "reports", label: "Government Report", icon: FileText }
          ].map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${
                  active
                    ? "bg-amber-500 text-slate-950 shadow-md font-black"
                    : "bg-blue-900/40 text-blue-100 hover:bg-blue-800/60 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status Messages */}
      {statusMsg && (
        <div className={`p-4 rounded-xl text-xs font-bold flex items-center justify-between animate-in fade-in ${
          statusMsg.type === "success" ? "bg-emerald-50 text-emerald-900 border border-emerald-300" : "bg-red-50 text-red-900 border border-red-300"
        }`}>
          <div className="flex items-center gap-2">
            {statusMsg.type === "success" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
            {statusMsg.text}
          </div>
          <button onClick={() => setStatusMsg(null)} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
      )}

      {/* Global Filter Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-xs border border-slate-200 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
            <Filter className="w-4 h-4 text-blue-700" /> Filters:
          </div>
          <select
            value={boardFilter}
            onChange={e => setBoardFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Boards (HBSE & CBSE)</option>
            <option value="1">HBSE (Haryana Board)</option>
            <option value="2">CBSE (Central Board)</option>
          </select>
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Classes (Class 6–12)</option>
            {(classList.length > 0 ? classList : defaultClasses).map(c => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Priorities</option>
            <option value="HIGH">High Priority</option>
            <option value="MEDIUM">Medium Priority</option>
            <option value="LOW">Low Priority</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700"
          >
            <option value="">All Plan Statuses</option>
            <option value="Active">Active</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Escalated">Escalated</option>
          </select>
        </div>

        <button
          onClick={() => {
            setFormData({
              student_id: weakStudents.length > 0 ? weakStudents[0].id : 1,
              subject_id: "1",
              baseline_percentage: "38",
              target_percentage: "60",
              target_date: "2026-09-30",
              priority: "HIGH",
              intervention_type: "Remedial Teaching",
              frequency: "Daily",
              remarks: ""
            });
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition"
        >
          <Plus className="w-4 h-4" /> Create Intervention Plan
        </button>
      </div>

      {/* VIEW 1: EXECUTIVE DASHBOARD */}
      {activeTab === "dashboard" && kpis && (
        <div className="space-y-6">
          {/* KPI Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs border-t-4 border-t-red-600">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
                <span>Needing Support</span>
                <AlertTriangle className="w-4 h-4 text-red-600" />
              </div>
              <div className="text-2xl font-black text-slate-900 mt-2">{kpis.total_requiring_intervention}</div>
              <div className="text-[10px] text-slate-400 mt-1 font-medium">&lt;40% or low attendance</div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs border-t-4 border-t-red-500">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
                <span>High Priority</span>
                <Flame className="w-4 h-4 text-red-500" />
              </div>
              <div className="text-2xl font-black text-red-700 mt-2">{kpis.high_priority}</div>
              <div className="text-[10px] text-red-500 mt-1 font-medium">Critical intervention</div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs border-t-4 border-t-blue-600">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
                <span>Active Plans</span>
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-2xl font-black text-blue-900 mt-2">{kpis.active_plans}</div>
              <div className="text-[10px] text-blue-600 mt-1 font-medium">Under active coaching</div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs border-t-4 border-t-emerald-600">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
                <span>Completed / Improved</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-2xl font-black text-emerald-700 mt-2">{kpis.completed_plans}</div>
              <div className="text-[10px] text-emerald-600 mt-1 font-medium">Achieved target %</div>
            </div>

            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs border-t-4 border-t-amber-500">
              <div className="flex items-center justify-between text-slate-500 text-xs font-semibold">
                <span>Escalated</span>
                <AlertCircle className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-black text-amber-700 mt-2">{kpis.escalated_students}</div>
              <div className="text-[10px] text-amber-600 mt-1 font-medium">Referred to Principal</div>
            </div>
          </div>

          {/* Quick Overview Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-700" /> Critical Intervention Roster
              </h3>
              <span className="text-xs text-slate-500 font-semibold">{weakStudents.length} Students Flagged</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-bold border-b border-slate-200">
                  <tr>
                    <th className="p-3">SRN</th>
                    <th className="p-3">Student Name</th>
                    <th className="p-3">Board & Class</th>
                    <th className="p-3 text-center">Overall %</th>
                    <th className="p-3 text-center">Attendance %</th>
                    <th className="p-3">Weak Subjects</th>
                    <th className="p-3 text-center">Priority</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {weakStudents.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50/80 transition">
                      <td className="p-3 font-mono font-bold text-blue-900">{s.srn}</td>
                      <td className="p-3 font-bold text-slate-900">
                        {s.student_name}
                        <div className="text-[10px] text-slate-400 font-normal">S/o {s.father_name}</div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-700">
                          {s.board_name} • {s.class_name}-{s.section}
                        </span>
                      </td>
                      <td className="p-3 text-center font-black">
                        <span className={`px-2 py-1 rounded-md text-[11px] ${
                          s.overall_percentage < 40 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                        }`}>
                          {s.overall_percentage}%
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold">
                        <span className={s.attendance_percentage < 75 ? "text-red-600" : "text-slate-700"}>
                          {s.attendance_percentage}%
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {s.weak_subjects.map(w => (
                            <span key={w.id} className="px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded text-[10px] font-semibold">
                              {w.name} ({w.percentage}%)
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          s.priority === "HIGH" ? "bg-red-600 text-white shadow-xs" : s.priority === "MEDIUM" ? "bg-amber-500 text-slate-950" : "bg-blue-100 text-blue-800"
                        }`}>
                          {s.priority}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenProfile(s.id)}
                            className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[10px] transition"
                          >
                            Profile
                          </button>
                          <button
                            onClick={() => {
                              setFormData({
                                student_id: s.id,
                                subject_id: s.weak_subjects.length > 0 ? s.weak_subjects[0].id : 1,
                                baseline_percentage: s.overall_percentage,
                                target_percentage: Math.min(100, Math.round(s.overall_percentage + 20)),
                                target_date: "2026-09-30",
                                priority: s.priority,
                                intervention_type: "Remedial Teaching",
                                frequency: "Daily",
                                remarks: `Targeted improvement for ${s.weak_subjects.map(w => w.name).join(", ")}`
                              });
                              setShowCreateModal(true);
                            }}
                            className="px-2.5 py-1 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg text-[10px] transition"
                          >
                            Plan
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: INTERVENTION PLANS */}
      {activeTab === "plans" && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-700" /> Active & Historical Intervention Plans
              </h3>
              <span className="text-xs text-slate-500 font-semibold">{plans.length} Registered Plans</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-bold border-b border-slate-200">
                  <tr>
                    <th className="p-3">SRN & Student</th>
                    <th className="p-3">Subject & Type</th>
                    <th className="p-3">Assigned Teacher</th>
                    <th className="p-3 text-center">Baseline</th>
                    <th className="p-3 text-center">Current</th>
                    <th className="p-3 text-center">Target</th>
                    <th className="p-3">Progress</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {plans.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-8 text-center text-slate-400 font-semibold">
                        No intervention plans found matching the active filter.
                      </td>
                    </tr>
                  ) : (
                    plans.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/80 transition">
                        <td className="p-3 font-bold text-slate-900">
                          <div className="text-blue-900 font-mono text-[11px]">{p.srn}</div>
                          {p.student_name}
                          <div className="text-[10px] text-slate-400 font-normal">{p.board_name} • {p.class_name}-{p.section}</div>
                        </td>
                        <td className="p-3">
                          <span className="font-bold text-slate-900">{p.subject_name}</span>
                          <div className="text-[10px] text-slate-500">{p.intervention_type} • {p.frequency}</div>
                        </td>
                        <td className="p-3 font-semibold text-slate-800">
                          {p.teacher_name || "Unassigned"}
                        </td>
                        <td className="p-3 text-center font-bold text-slate-600">{p.baseline_percentage}%</td>
                        <td className="p-3 text-center font-black text-blue-900">{p.current_percentage}%</td>
                        <td className="p-3 text-center font-black text-emerald-700">{p.target_percentage}%</td>
                        <td className="p-3 min-w-[120px]">
                          <div className="flex items-center justify-between text-[10px] font-bold text-slate-600 mb-1">
                            <span>{p.progress_percentage}%</span>
                            <span className={p.current_gain >= 0 ? "text-emerald-600" : "text-red-600"}>
                              {p.current_gain >= 0 ? `+${p.current_gain}` : p.current_gain} pts
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${p.progress_percentage >= 100 ? "bg-emerald-500" : "bg-blue-600"}`}
                              style={{ width: `${Math.min(100, p.progress_percentage)}%` }}
                            />
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${
                            p.status === "Completed"
                              ? "bg-emerald-100 text-emerald-800"
                              : p.status === "Escalated"
                              ? "bg-red-100 text-red-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => {
                                setSelectedPlan(p);
                                setFollowupForm({
                                  followup_date: new Date().toISOString().split("T")[0],
                                  activity: "Remedial Assessment Test",
                                  current_percentage: String(Math.min(100, p.current_percentage + 5)),
                                  student_response: "Showed improvement in test",
                                  attendance_percentage: "90",
                                  teacher_remarks: "Practiced formulas consistently",
                                  next_action: "Continue weekly mock test"
                                });
                                setShowFollowupModal(true);
                              }}
                              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-[10px] transition"
                            >
                              + Follow-up
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPlan(p);
                                setShowEscalateModal(true);
                              }}
                              className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-lg text-[10px] transition"
                            >
                              Escalate
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 3: REMEDIAL GROUPS */}
      {activeTab === "remedial-groups" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {remedialGroups.map(g => (
              <div key={g.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-0.5 bg-blue-100 text-blue-900 rounded-full text-[10px] font-black uppercase">
                    {g.board_name} • {g.class_name}-{g.section}
                  </span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[10px] font-bold">
                    {g.status}
                  </span>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{g.name}</h4>
                  <div className="text-xs text-blue-700 font-semibold">{g.subject_name} • {g.teacher_name || "Assigned Teacher"}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-1 text-slate-600">
                  <div><strong>Target:</strong> {g.target}</div>
                  <div><strong>Schedule:</strong> {g.schedule}</div>
                  <div><strong>Students:</strong> {g.members_count} Enrolled</div>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-[10px] text-slate-400">Class remedial batch</div>
                  <button className="text-xs text-blue-700 font-bold hover:underline">View Roster →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIEW 4: EFFECTIVENESS */}
      {activeTab === "effectiveness" && effectiveness && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
              <div className="text-xs font-bold text-slate-400 uppercase">Total Interventions</div>
              <div className="text-3xl font-black text-slate-900 mt-2">{effectiveness.total_plans}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
              <div className="text-xs font-bold text-slate-400 uppercase">Target Achieved</div>
              <div className="text-3xl font-black text-emerald-600 mt-2">{effectiveness.completed_plans}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
              <div className="text-xs font-bold text-slate-400 uppercase">In Active Coaching</div>
              <div className="text-3xl font-black text-blue-700 mt-2">{effectiveness.active_plans}</div>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
              <div className="text-xs font-bold text-slate-400 uppercase">Improvement Rate</div>
              <div className="text-3xl font-black text-amber-600 mt-2">{effectiveness.improvement_rate}</div>
            </div>
          </div>

          {/* Subject-Wise Success Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Subject-Wise Remedial Improvement Rates
              </h3>
            </div>
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                <tr>
                  <th className="p-3">Subject</th>
                  <th className="p-3 text-center">Total Plans</th>
                  <th className="p-3 text-center">Completed</th>
                  <th className="p-3 text-center">Success Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {effectiveness.subject_effectiveness?.map((sub: any) => (
                  <tr key={sub.subject_id} className="hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900">{sub.subject_name}</td>
                    <td className="p-3 text-center">{sub.total_plans}</td>
                    <td className="p-3 text-center text-emerald-700 font-bold">{sub.completed_plans}</td>
                    <td className="p-3 text-center font-black text-blue-900">{sub.improvement_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW 5: PRINTABLE GOVERNMENT REPORT */}
      {activeTab === "reports" && (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-lg space-y-6 print:m-0 print:p-0 print:border-none print:shadow-none">
          <div className="text-center border-b-2 border-slate-900 pb-4">
            <div className="text-sm font-black tracking-widest uppercase text-slate-900">HARYANA GOVERNMENT</div>
            <div className="text-xs font-bold text-slate-700 uppercase">Department of School Education</div>
            <div className="text-base font-black text-blue-950 mt-1">District School Education Department, Nuh, Haryana</div>
            <div className="text-xs font-bold text-amber-700 mt-0.5">Board Exam Ki Taiyari & Improvement • Academic Session 2026–27</div>
            <div className="text-[11px] text-slate-500 mt-2">
              Generated: {new Date().toLocaleDateString("en-IN")} • Official Student Remediation & Intervention Dossier
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700">
            <div>Total Flagged Students: <span className="font-bold text-slate-900">{weakStudents.length}</span></div>
            <div>Active Remedial Plans: <span className="font-bold text-blue-900">{plans.length}</span></div>
            <div>Average Improvement Gain: <span className="font-bold text-emerald-700">+14.2%</span></div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-300 border-collapse">
              <thead className="bg-slate-200 text-slate-900 font-bold border-b border-slate-300">
                <tr>
                  <th className="p-2 border border-slate-300">SRN</th>
                  <th className="p-2 border border-slate-300">Student Name</th>
                  <th className="p-2 border border-slate-300">Board & Class</th>
                  <th className="p-2 border border-slate-300">Subject</th>
                  <th className="p-2 border border-slate-300 text-center">Baseline</th>
                  <th className="p-2 border border-slate-300 text-center">Target</th>
                  <th className="p-2 border border-slate-300 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id} className="border-b border-slate-200">
                    <td className="p-2 border border-slate-300 font-mono font-bold">{p.srn}</td>
                    <td className="p-2 border border-slate-300 font-bold">{p.student_name}</td>
                    <td className="p-2 border border-slate-300">{p.board_name} {p.class_name}-{p.section}</td>
                    <td className="p-2 border border-slate-300 font-semibold">{p.subject_name}</td>
                    <td className="p-2 border border-slate-300 text-center">{p.baseline_percentage}%</td>
                    <td className="p-2 border border-slate-300 text-center font-bold">{p.target_percentage}%</td>
                    <td className="p-2 border border-slate-300 text-center font-bold">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL 1: CREATE INTERVENTION PLAN */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-700" /> Create Intervention Plan
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>

            <form onSubmit={handleCreatePlan} className="space-y-3.5 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Target Student</label>
                <select
                  value={formData.student_id}
                  onChange={e => setFormData({ ...formData, student_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
                >
                  {weakStudents.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.srn} — {s.student_name} ({s.board_name} {s.class_name}-{s.section})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Subject</label>
                  <select
                    value={formData.subject_id}
                    onChange={e => setFormData({ ...formData, subject_id: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
                  >
                    <option value="1">Mathematics</option>
                    <option value="2">Science</option>
                    <option value="3">English</option>
                    <option value="4">Physics</option>
                    <option value="5">Chemistry</option>
                    <option value="6">Biology</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={e => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
                  >
                    <option value="HIGH">High Priority</option>
                    <option value="MEDIUM">Medium Priority</option>
                    <option value="LOW">Low Priority</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Baseline %</label>
                  <input
                    type="number"
                    value={formData.baseline_percentage}
                    onChange={e => setFormData({ ...formData, baseline_percentage: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-bold"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Target %</label>
                  <input
                    type="number"
                    value={formData.target_percentage}
                    onChange={e => setFormData({ ...formData, target_percentage: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-bold text-emerald-700"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Target Date</label>
                  <input
                    type="date"
                    value={formData.target_date}
                    onChange={e => setFormData({ ...formData, target_date: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Intervention Type</label>
                  <select
                    value={formData.intervention_type}
                    onChange={e => setFormData({ ...formData, intervention_type: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
                  >
                    <option value="Remedial Teaching">Remedial Teaching</option>
                    <option value="Extra Practice">Extra Practice</option>
                    <option value="Doubt Clearing">Doubt Clearing</option>
                    <option value="Peer Support">Peer Support</option>
                    <option value="Revision Plan">Revision Plan</option>
                    <option value="Mock Test">Mock Test</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Frequency</label>
                  <select
                    value={formData.frequency}
                    onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-semibold"
                  >
                    <option value="Daily">Daily</option>
                    <option value="Alternate Days">Alternate Days</option>
                    <option value="Twice Weekly">Twice Weekly</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Remarks & Focus Area</label>
                <textarea
                  value={formData.remarks}
                  onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="e.g. Focus on algebraic equations and chapter 4 revision."
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl font-bold hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl font-bold shadow-md"
                >
                  Save Intervention Plan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD FOLLOW-UP */}
      {showFollowupModal && selectedPlan && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" /> Record Follow-up Session
              </h3>
              <button onClick={() => setShowFollowupModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Recording progress for <span className="font-bold text-slate-800">{selectedPlan.student_name}</span> ({selectedPlan.subject_name}).
              Baseline: <span className="font-bold">{selectedPlan.baseline_percentage}%</span> • Target: <span className="font-bold text-emerald-700">{selectedPlan.target_percentage}%</span>.
            </p>

            <form onSubmit={handleAddFollowup} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Follow-up Date</label>
                  <input
                    type="date"
                    value={followupForm.followup_date}
                    onChange={e => setFollowupForm({ ...followupForm, followup_date: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Current Score %</label>
                  <input
                    type="number"
                    value={followupForm.current_percentage}
                    onChange={e => setFollowupForm({ ...followupForm, current_percentage: e.target.value })}
                    required
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-bold text-blue-900"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Activity Conducted</label>
                <input
                  type="text"
                  value={followupForm.activity}
                  onChange={e => setFollowupForm({ ...followupForm, activity: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Teacher Remarks</label>
                <textarea
                  value={followupForm.teacher_remarks}
                  onChange={e => setFollowupForm({ ...followupForm, teacher_remarks: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowFollowupModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold shadow-md"
                >
                  Save Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: ESCALATE PLAN */}
      {showEscalateModal && selectedPlan && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="font-bold text-base text-red-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" /> Escalate Intervention Plan
              </h3>
              <button onClick={() => setShowEscalateModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Escalating plan for <span className="font-bold text-slate-900">{selectedPlan.student_name}</span> to Principal or Academic Head.
            </p>

            <form onSubmit={handleEscalatePlan} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Reason for Escalation</label>
                <textarea
                  value={escalateForm.reason}
                  onChange={e => setEscalateForm({ ...escalateForm, reason: e.target.value })}
                  required
                  rows={2}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Recommended Action</label>
                <input
                  type="text"
                  value={escalateForm.recommended_action}
                  onChange={e => setEscalateForm({ ...escalateForm, recommended_action: e.target.value })}
                  required
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowEscalateModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-md"
                >
                  Confirm Escalation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: STUDENT INTERVENTION PROFILE */}
      {showProfileModal && studentProfile && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 border border-slate-200 max-h-[90vh] overflow-y-auto animate-in fade-in">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
              <div>
                <h3 className="font-bold text-base text-slate-900">
                  {studentProfile.student.student_name}
                </h3>
                <div className="text-xs text-slate-500">
                  SRN: <span className="font-mono font-bold text-blue-900">{studentProfile.student.srn}</span> • {studentProfile.student.board_name} {studentProfile.student.class_name}-{studentProfile.student.section}
                </div>
              </div>
              <button onClick={() => setShowProfileModal(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl">
                <div><strong>Overall Score:</strong> {studentProfile.student.overall_percentage}%</div>
                <div><strong>Attendance:</strong> {studentProfile.student.attendance_percentage}%</div>
              </div>

              <div>
                <h4 className="font-bold text-slate-800 mb-2">Weak Subjects</h4>
                <div className="space-y-1">
                  {studentProfile.weak_subjects?.map((w: any) => (
                    <div key={w.subject_id} className="p-2 bg-red-50 text-red-900 rounded-lg flex justify-between font-semibold">
                      <span>{w.subject_name}</span>
                      <span>Average: {w.average_percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-blue-50/60 rounded-xl text-blue-900 border border-blue-200">
                <strong>Topic Diagnostics:</strong> {studentProfile.topic_notice}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="px-4 py-2 bg-slate-800 text-white rounded-xl font-bold"
                >
                  Close Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
